import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Wrench, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";

const DEPTS = [
  { value: "kesim", label: "Kesim" },
  { value: "baski", label: "Baskı" },
  { value: "nakis", label: "Nakış" },
  { value: "dikim", label: "Dikim" },
  { value: "kalite", label: "Kalite Kontrol" },
  { value: "paketleme", label: "Paketleme" },
  { value: "sevkiyat", label: "Sevkiyat" },
];

const empty = { name: "", department: "dikim", minutes_per_unit: 5, cost_per_minute: 5, description: "" };

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Operations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/operations");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (op) => {
    setEditingId(op.id);
    setForm({
      name: op.name, department: op.department, minutes_per_unit: op.minutes_per_unit,
      cost_per_minute: op.cost_per_minute, description: op.description || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        department: form.department,
        minutes_per_unit: Number(form.minutes_per_unit),
        cost_per_minute: Number(form.cost_per_minute),
        description: form.description || "",
      };
      if (editingId) {
        await api.patch(`/operations/${editingId}`, payload);
        toast.success("Operasyon güncellendi");
      } else {
        await api.post(`/operations`, payload);
        toast.success("Operasyon eklendi");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (op) => {
    if (!window.confirm(`${op.name} silinsin mi?`)) return;
    try {
      await api.delete(`/operations/${op.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = DEPTS.map((d) => ({
    ...d,
    items: items.filter((i) => i.department === d.value),
  })).filter((g) => g.items.length > 0);

  return (
    <div>
      <PageHeader
        title="Operasyon Kütüphanesi"
        subtitle="Reçetelerde tekrar kullanılan üretim işlemleri (SMV & birim işçilik)"
        breadcrumbs={["Modüller", "Operasyonlar"]}
        actions={
          <Button data-testid="add-operation-btn" onClick={openNew}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Yeni Operasyon
          </Button>
        }
      />

      <div className="p-6 lg:p-8">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 rounded-sm p-12 text-center">
            <Wrench className="w-10 h-10 mx-auto text-slate-700 mb-3" />
            <div className="text-slate-400 text-sm">Henüz operasyon yok. İlkini ekleyerek başlayın.</div>
          </Card>
        ) : (
          <div className="space-y-6">
            {grouped.map((g) => (
              <Card key={g.value} className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden" data-testid={`operation-group-${g.value}`}>
                <div className="px-5 py-3 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-blue-500" />
                    <div className="text-sm font-semibold text-slate-100 tracking-tight uppercase">{g.label}</div>
                    <Badge className="bg-slate-800 text-slate-300 border border-slate-700 rounded-sm text-[10px] uppercase tracking-wider">
                      {g.items.length} işlem
                    </Badge>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">dk/adet</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">₺/dk</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Birim İşçilik ₺</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.items.map((op) => (
                      <TableRow key={op.id} className="border-slate-800" data-testid={`operation-row-${op.id}`}>
                        <TableCell>
                          <div className="text-sm font-medium text-slate-100">{op.name}</div>
                          {op.description && (
                            <div className="text-xs text-slate-500 mt-0.5">{op.description}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 font-mono text-sm text-slate-300">
                            <Clock className="w-3 h-3" /> {fmt(op.minutes_per_unit)} dk
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-300">₺{fmt(op.cost_per_minute)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-blue-400 font-semibold">
                          ₺{fmt(op.minutes_per_unit * op.cost_per_minute)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(op)}
                            data-testid={`edit-operation-${op.id}`}
                            className="text-slate-400 hover:text-slate-100 rounded-sm">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(op)}
                            data-testid={`delete-operation-${op.id}`}
                            className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">
              {editingId ? "Operasyonu Düzenle" : "Yeni Operasyon"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Ad *</Label>
              <Input required value={form.name} data-testid="operation-name-input"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Departman</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="operation-dept-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {DEPTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">dk/Adet</Label>
                <Input type="number" step="0.1" value={form.minutes_per_unit} data-testid="operation-minutes-input"
                  onChange={(e) => setForm({ ...form, minutes_per_unit: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">₺/dk</Label>
                <Input type="number" step="0.1" value={form.cost_per_minute} data-testid="operation-rate-input"
                  onChange={(e) => setForm({ ...form, cost_per_minute: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm font-mono focus-visible:ring-blue-500" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Açıklama</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm resize-none focus-visible:ring-blue-500" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="save-operation-btn"
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
